import { useEffect, useRef, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import SearchBox from '@/components/feature/SearchBox';

const navLinks = [
  { to: '/', label: '首页' },
  { to: '/about', label: '关于' },
  { to: '/contact', label: '联系我们' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setCategoryOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `relative whitespace-nowrap px-3 py-2 text-sm font-medium transition-colors ${
      isActive ? 'text-primary-600' : 'text-foreground-600 hover:text-primary-600'
    }`;

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-colors ${
        scrolled ? 'border-background-200 bg-background-50/95 backdrop-blur' : 'border-transparent bg-background-50'
      }`}
    >
      <div className="flex h-16 w-full items-center gap-4 px-4 md:px-8">
        <Link to="/" className="flex flex-shrink-0 items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-compass-3-line text-lg"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg font-bold tracking-tight text-foreground-950">网址大全</span>
            <span className="mt-0.5 hidden text-[10px] text-foreground-400 sm:block">权威综合资源目录</span>
          </span>
        </Link>

        <nav className="hidden items-center md:flex">
          {navLinks.slice(0, 1).map((link) => (
            <NavLink key={link.to} to={link.to} className={linkClass} end>
              {link.label}
            </NavLink>
          ))}

          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setCategoryOpen((prev) => !prev)}
              className={`flex items-center gap-1 whitespace-nowrap px-3 py-2 text-sm font-medium transition-colors ${
                categoryOpen ? 'text-primary-600' : 'text-foreground-600 hover:text-primary-600'
              }`}
            >
              分类导航
              <i className={`ri-arrow-down-s-line transition-transform ${categoryOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {categoryOpen && (
              <div className="absolute left-0 top-full mt-2 w-[520px] rounded-lg border border-background-200 bg-background-50 p-4">
                <div className="grid grid-cols-3 gap-1">
                  {categories.map((category) => (
                    <Link
                      key={category.id}
                      to={`/category/${category.id}`}
                      onClick={() => setCategoryOpen(false)}
                      className="flex items-center gap-2 rounded-md px-2.5 py-2 text-sm text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-700"
                    >
                      <i className={`${category.icon} text-primary-500`}></i>
                      <span className="truncate">{category.name}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {navLinks.slice(1).map((link) => (
            <NavLink key={link.to} to={link.to} className={linkClass}>
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto hidden w-[280px] lg:block">
          <SearchBox placeholder="搜索网站或关键词…" />
        </div>
        <Link
          to="/about"
          className="ml-auto hidden whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 lg:hidden md:ml-auto md:inline-block lg:ml-0"
        >
          加入收录
        </Link>

        <button
          type="button"
          aria-label="打开菜单"
          onClick={() => setMobileOpen(true)}
          className="ml-auto flex h-10 w-10 items-center justify-center rounded-md text-foreground-700 md:hidden"
        >
          <i className="ri-menu-3-line text-xl"></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/40"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          ></div>
          <div className="absolute right-0 top-0 flex h-full w-[80%] max-w-xs flex-col overflow-y-auto bg-background-50 p-4">
            <div className="flex items-center justify-between">
              <span className="font-heading text-lg font-bold text-foreground-950">网址大全</span>
              <button
                type="button"
                aria-label="关闭菜单"
                onClick={() => setMobileOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-600"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="mt-4">
              <SearchBox placeholder="搜索网站…" />
            </div>

            <nav className="mt-6 flex flex-col">
              {navLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.to === '/'}
                  onClick={() => setMobileOpen(false)}
                  className="rounded-md px-3 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100"
                >
                  {link.label}
                </NavLink>
              ))}
            </nav>

            <p className="mt-6 px-3 text-xs font-medium uppercase tracking-wide text-foreground-400">分类导航</p>
            <div className="mt-2 grid grid-cols-2 gap-1">
              {categories.map((category) => (
                <Link
                  key={category.id}
                  to={`/category/${category.id}`}
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100"
                >
                  <i className={`${category.icon} text-primary-500`}></i>
                  <span className="truncate">{category.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}